(ns app.domain.fee.model
  #?(:clj (:import [java.util Date]))
  (:require #?(:clj [app.money :as money]
               :cljs [app.front.util :as futil])
            [app.domain.group.model :as group-model]
            [app.domain.template.model :as template-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.message.model :as message-model]))

(def fee-schedule-editable-keys
  #{:fee-schedule/titles
    :fee-schedule/permit-type
    :fee-schedule/valid-from})


(def fee-template-editable-keys
  #{:fee-template/titles
    :fee-template/type
    :bill/phase
    :fee-template/group
    :fee-template/minimum-total
    :fee-template/unit-price-formula
    :fee-template/quantity-formula
    :fee-template/quantity
    :fee-template/allow-including-in-property-tax?
    :fee-template/external-fee-code
    :fee-template/general-ledger-code
    :fee-template/bond-payment?
    :fee-template/refundable?
    :fee-template/work-order-number?
    :fee-template/date-rule
    :fee-template/allow-penalty?
    :fee-template/apply-grace-period?
    :fee-template/penalty-rate
    :fee-template/penalty-rate-type
    :fee-template/interest-rate-type
    :fee-template/interest-rate
    :fee-template/interest-recurrence
    :fee-template/interest-occurrence-limit})


(def fee-variable-editable-keys
  #{:fee-variable/titles
    :fee-variable/identifier
    :fee-variable/source-field
    :fee-variable/source-type
    :fee-variable/fee-template
    :fee-variable/fee-formula})

(def fee-variable-source-types
  #{:fee-variable.source-type/form
    :fee-variable.source-type/fee
    :fee-variable.source-type/formula})

(def fee-conditional-editable-keys
  #{:fee-conditional/comparator
    :fee-conditional/compare-value
    :fee-conditional/return-value})

(def fee-date-rules
  "The calculation rule for date-based fees (i.e. late fees)"
  [:fee-template.date-rule/one-time
   :fee-template.date-rule/recurring-monthly])

(def fee-group-editable-keys
  #{:fee-group/titles})


(def concerned-roles
  #{:role/property-owner
    :role/payer
    :role/applicant})

(def bill-states
  [:bill.state/draft
   :bill.state/unpaid
   :bill.state/pending-confirmation
   :bill.state/paid
   :bill.state/canceled
   :bill.state/reversed])

(def phases
  [:bill.phase/permit
   :bill.phase/upfront
   :bill.phase/deposit
   :bill.phase/additional
   :bill.phase/pre-consultation-request
   :bill.phase/pre-consultation])

(defn penalty-allowed-for-phase? [phase]
  (not= :bill.phase/deposit phase))

(def rates
  [:fee-rate.type/percentage
   :fee-rate.type/fixed])

(def interest-recurrences
  [:fee-template.interest-recurrence/daily
   :fee-template.interest-recurrence/weekly
   :fee-template.interest-recurrence/monthly])

(def late-fee-types
  #{:fee.type/penalty
    :fee.type/interest})

(defn penalty-fee? [fee]
  (= (:fee/type fee) :fee.type/penalty))

(defn interest-fee? [fee]
  (= (:fee/type fee) :fee.type/interest))

(defn fee-waived?
  "Returns true if the fee has been waived."
  [fee]
  (= :fee.state/waived (:domain/state fee)))

(defn can-waive-fee?
  "Returns true if the fee can be waived (unpaid penalty or interest fee)."
  [fee]
  (and (late-fee-types (:fee/type fee))
       (= :fee.state/unpaid (:domain/state fee))))

(defn can-restore-fee?
  "Returns true if the fee can be restored (waived penalty or interest fee)."
  [fee]
  (and (late-fee-types (:fee/type fee))
       (= :fee.state/waived (:domain/state fee))))

(defn visible-on-pdf? [fee]
  (or (not (late-fee-types (:fee/type fee)))
      (and (not (zero? (:fee/amount fee)))
           (not= :fee.state/waived (:domain/state fee)))))

(def fee-variable-identifier-pattern ":[a-z0-9_]+")

(def fee-number-string-pattern #"-?\d{1,10}(\.\d+)?")

(defn valid-schedule?
  ([schedule]
   (valid-schedule? #?(:clj (Date.)
                       :cljs (js/Date.))
                    schedule))
  ([now schedule]
   (let [valid-from (:fee-schedule/valid-from schedule)]
     (and (some? valid-from)
          (= 1 (compare now valid-from))))))

(defn bill-amount [bill]
  (->> bill
       :bill/fees
       (map :fee/amount)
       (reduce +)))

(defn payments [bill]
  (->> bill
       :entity/_parent
       (filter #(-> % :entity/type (= :entity.type/payment)))))

(def ^{:arglists '([fee-schedule-or-structure])} child-fee-structures
  (child/child-entities-of-type-fn :entity.type/fee-structure))

(def ^{:arglists '([fee-schedule])} schedule-fee-templates
  (child/child-entities-of-type-fn :entity.type/fee-template))

(def ^{:arglists '([fee-schedule])} schedule-fee-groups
  (child/child-entities-of-type-fn :entity.type/fee-group))

(def ^{:arglists '([fee-schedule])} schedule-fee-variables
  (child/child-entities-of-type-fn :entity.type/fee-variable))

(def ^{:arglists '([fee-schedule])} schedule-reversal-method-assignments
  "Get reversal method assignments for a fee schedule using the reverse reference.
   This is a bridge entity that connects schedules and reversal methods."
  :schedule-reversal-method-assignment/_schedule)

(def ^{:arglists '([domain-group])} group-escrow-accounts
  "Get escrow accounts linked to a domain group. Note that domain group is
   the parent of an authority group. Escrow accounts are child entities of
   a domain group."
  (child/child-entities-of-type-fn :entity.type/escrow-account))

(def ^{:arglists '([escrow-account])} escrow-account-transactions
  (child/child-entities-of-type-fn :entity.type/escrow-transaction))

(defn last-payment [bill]
  (->> bill
       payments
       (sort-by :payment/paid-ts)
       last))

(defn last-payment-ts [bill]
  (some->> bill
           payments
           (#(or (seq (keep :payment/reported-payment-date %))
                 (seq (keep :payment/paid-ts %))))
           sort
           last))

(def shared-bill-template-variables ["phase"
                                     "total-amount"
                                     "bill-nr"
                                     "fee-groups"])

(def shared-receipt-template-variables (concat shared-bill-template-variables
                                               ["date-paid"]))

(def receipt-template-variables
  (-> ["header"
       "phase"
       "date-paid"
       "bill-issued-date"
       "bill-due-date"
       "total-amount"
       "payer-name"
       "payer-addr"
       "payer-zip"
       "payer-city"
       "payer-province"
       "payer-company-name"
       "payee-name"
       "payee-addr"
       "payee-zip"
       "payee-city"
       "payee-province"
       "payee-company-name"
       "bill-nr"
       "payment-method"
       "cheque-number"
       "check-number"
       "gl-code-column-class"
       "fee-groups"
       "workspace-addr"]
      (into template-model/address-placeholders)
      (into ["workspace-cloudpermit-id"
             "workspace-municipality"
             "workspace-reference-label"
             "workspace-reference-number"
             "workspace-permit-number"
             "property-attributes"
             "property-roll-number"
             "payment-external-id"
             "footnotes"
             "additional-payment-info"
             "reversal-method"
             "licence-number"])))

(def fee-group-snippet-variables ["group-name"
                                  "fee-items"])


;;  Special variables are placeholders like for example css classes.
;;  They are part of a template but they can't be selected in snippets.

(def fee-item-special-variables
  ["fee-class"])

(def receipt-special-variables
  ["cheque-number-class"
   "check-number-class"
   ;; toggles GL column visibility on UL, should not be suggested in editors
   "gl-code-column-class"])


(def fee-item-snippet-variables ["title"
                                 "description"
                                 "quantity"
                                 "unit-price"
                                 "amount"
                                 "refundable"
                                 "gl-code"])

(def bill-template-variables
  (-> ["header"
       "phase"
       "bill-issued-date"
       "bill-due-date"
       "total-amount"
       "payer-name"
       "payer-addr"
       "payer-zip"
       "payer-city"
       "payer-province"
       "payer-company-name"
       "payee-name"
       "payee-addr"
       "payee-zip"
       "payee-city"
       "payee-province"
       "payee-company-name"
       "bill-nr"
       "gl-code-column-class"
       "fee-groups"
       "workspace-addr"]
      (into template-model/address-placeholders)
      (into ["workspace-cloudpermit-id"
             "workspace-municipality"
             "workspace-reference-label"
             "workspace-reference-number"
             "workspace-permit-number"
             "property-attributes"
             "property-roll-number"
             "footnotes"
             "reversal-method"])))

(def bill-group-snippet-variables ["group-name"
                                   "fee-items"])

(defn receipt-picker-variables
  "Hide unit-related placeholders when `:feature-flag.id/property-db-units` is off."
  [feature-flags]
  (template-model/filter-unit-placeholders receipt-template-variables feature-flags))

(defn bill-picker-variables
  "Hide unit-related placeholders when `:feature-flag.id/property-db-units` is off."
  [feature-flags]
  (template-model/filter-unit-placeholders bill-template-variables feature-flags))

(def bill-item-snippet-variables ["title"
                                  "description"
                                  "quantity"
                                  "unit-price"
                                  "amount"
                                  "refundable"
                                  "gl-code"])

(defn collect-footnotes [fees]
  (->> fees
       (filter :fee-template/date-rule)
       (sort-by (juxt
                  ;; type of date aka the text
                  :fee-template/date-reference
                  ;; the date itself
                  :fee/reference-date))
       (map-indexed (fn [ix {:fee-template/keys [date-reference] :fee/keys [reference-date]}]
                      [[date-reference reference-date]
                       (-> ix inc (repeat "*") str/join)]))
       (reduce (fn [acc [k v]]
                 (if (contains? acc k)
                   acc
                   (assoc acc k v)))
               (sorted-map))))

(defn footnote-reference-text [footnotes {:fee/keys          [reference-date date-multiplier]
                                          :fee-template/keys [date-reference date-rule]}]
  (case date-rule
    :fee-template.date-rule/one-time
    (get footnotes [date-reference reference-date])
    (str "×" date-multiplier " " (get footnotes [date-reference reference-date]))))

(def ^{:arglists '([fee-template lang])} fee-template-title
  "Get fee template title in a given language"
  (i18n-model/translation-fallback-fn :fee-template/titles :fee-template/title))

(def ^{:arglists '([fee-schedule lang])} fee-schedule-title
  "Get fee schedule title in a given language"
  (i18n-model/translation-fallback-fn :fee-schedule/titles :fee-schedule/title))

(defn fee-schedule-has-no-title?
  "Checks whether the fee-schedule has a title"
  [fee-schedule]
  (i18n-model/blank? (or (:fee-schedule/titles fee-schedule)
                         ;; support legacy title attribute, because we may have an
                         ;; unpublished schedule that was created before i18n
                         ;; implementation
                         (:fee-schedule/title fee-schedule))))

(def ^{:arglists '([fee-variable lang])} fee-variable-title
  "Get fee variable title in a given language"
  (i18n-model/translation-fallback-fn :fee-variable/titles :fee-variable/title))

(def ^{:arglists '([fee-group lang])} fee-group-title
  "Get fee variable title in a given language"
  (i18n-model/translation-fallback-fn :fee-group/titles :fee-group/title))

(def common-fee-template-i18n-attrs [:fee-template/titles])

(defmethod i18n-model/get-localized-string-attrs :entity.type/fee-template
  [_] common-fee-template-i18n-attrs)

(defmethod i18n-model/get-localized-string-attrs :entity.type/fee
  [_] common-fee-template-i18n-attrs)

(defmethod i18n-model/get-localized-string-attrs :entity.type/fee-schedule
  [_] [:fee-schedule/titles])

(defmethod i18n-model/get-localized-string-attrs :entity.type/fee-variable
  [_] [:fee-variable/titles])

(defmethod i18n-model/get-localized-string-attrs :entity.type/fee-group
  [_] [:fee-group/titles])

(defmethod i18n-model/get-localized-string-attrs :entity.type/bill-reversal-method
  [_] [:bill-reversal-method/titles :bill-reversal-method/messages])

(defmethod group-model/get-authority-group :entity.type/fee-template
  [fee-template]
  (-> fee-template :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/fee-schedule
  [fee-schedule]
  (-> fee-schedule :entity/parent))

(defmethod group-model/get-authority-group :entity.type/fee
  [fee]
  (-> fee :bill/_fees group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/bill
  [bill]
  (-> bill :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/fee-variable
  [fee-variable]
  (-> fee-variable :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/fee-group
  [fee-group]
  (-> fee-group :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/bill-reversal-method
  [reversal-method]
  (:bill-reversal-method/authority-group reversal-method))

(defn in-phase-fn
  "Given a set of phases, return a predicate for checking if a bill is in one of the phases"
  [phases]
  (fn [{:bill/keys [phase]}]
    (phases phase)))

(defn has-bill-pdf?
  [bill]
  (some? (:bill/bill-pdf-document bill)))

(defn has-bill-pdf-messages?
  "Return true if we've sent messages requesting bill PDF generation"
  [bill]
  (-> bill
      (message-model/find-messages
        (message-model/has-handler-fn :app.domain.application.message/bill-pdf-package-ready))
      seq
      boolean))

(defn can-select-applicant-as-default-payer?
  "Non-confirmed bills can use the applicant as the default payer"
  [phase]
  (some? (#{:bill.phase/upfront
            :bill.phase/deposit
            :bill.phase/pre-consultation-request} phase)))

(defn bill-workspace [bill]
  (-> bill :entity/parent :entity/parent))

(defn bill->case-id [bill]
  (-> bill
      bill-workspace
      workspace-model/case-id))

(def ^{:arglists '([phase-entity])} phase-bills
  (child/child-entities-of-type-fn :entity.type/bill))

(defn currency-of-bill [bill]
  (-> bill bill-workspace :domain/authority-group :group/currency))

(defn currency-of-fee [fee]
  (-> fee :bill/_fees currency-of-bill))

(defn workspace-bills [workspace]
  (->> workspace
       workspace-model/phase-entities
       (mapcat phase-bills)))

(defn back-end-sendable-workspace-bills [workspace]
  (->> workspace
       workspace-bills
       (filter (du/in-state-fn :bill.state/paid :bill.state/unpaid))
       (remove (some-fn :invoice/external-id ;; Do not re-send
                        :bill/legacy-bill?
                        :bill/exclude-in-integration?))
       seq))

(defn fee-schedule-for-structure [fee-structure]
  (if (= (:entity/type fee-structure) :entity.type/fee-schedule)
    fee-structure
    (recur (:entity/parent fee-structure))))

(def ^{:arglists '([authority-group])} group-schedules
  (child/child-entities-of-type-fn :entity.type/fee-schedule))

(defn unpaid-bills-for-workspace [workspace]
  (->> workspace
       workspace-bills
       (filter (du/in-state-fn :bill.state/unpaid))
       seq))

(def ^{:arglists '([bill])} confirmed-bill?
  "Returns true if the bill is in a confirmed state (unpaid or pending-confirmation)."
  (du/in-state-fn :bill.state/unpaid :bill.state/pending-confirmation))

(defn bill-invoice-number [bill]
  (or (:bill/automatic-invoice-number bill)
      (:bill/invoice-number bill)))

(def ^{:arglists '([bill])} bill-id
  "Either the bill's invoice number or :db/id as string"
  (comp str (some-fn bill-invoice-number
                     :db/id)))

(def ^{:arglists '([phase-entity])} hide-bills-from-applicant?
  "Is the phase-entity in a state where bills are hidden?"
  (du/in-state-fn :state.application/draft
                  :pre-consultation.state/draft))

(defn bill-rows-with-penalties-and-interests
  "Given a collection of fee rows, returns a collection of rows with penalty and interest fees
   grouped with the normal fees. The normal fees are returned first, followed by any penalty or interest fees."
  [rows]
  (->> rows
       (remove #(late-fee-types (:fee/type %)))
       (mapcat (fn [row]
                 (filter some? [row
                                (:fee/penalty-fee row)
                                (:fee/interest-fee row)])))))

(defn- get-fee-rate-keys
  "Returns the appropriate rate keys based on fee type."
  [fee]
  (cond
    (penalty-fee? fee)
    {:rate-type-k  :fee-template/penalty-rate-type
     :rate-k       :fee-template/penalty-rate
     :recurrence-k nil}
    (interest-fee? fee)
    {:rate-type-k  :fee-template/interest-rate-type
     :rate-k       :fee-template/interest-rate
     :recurrence-k :fee-template/interest-recurrence}))

(defn- format-rate-value
  "Formats a rate value according to its type."
  [rate-type rate-val currency]
  (case rate-type
    :fee-rate.type/fixed
    #?(:clj (money/money-string (int rate-val) currency)
       :cljs (futil/money-string rate-val currency))
    :fee-rate.type/percentage
    (str rate-val "%")))

(defn- format-rate-with-recurrence
  "Formats rate string with optional recurrence information."
  [rate-str recurrence-k original-fee tr]
  (if recurrence-k
    (str rate-str " / " (tr [(recurrence-k original-fee)]))
    rate-str))

(defn late-fee-config-note
  "Generates a descriptive string for a late fee.
   Handles both penalty and interest-based late fees."
  [{:fee/keys [original-fee] :as fee} tr currency]
  (let [{:keys [rate-type-k rate-k recurrence-k]} (get-fee-rate-keys fee)
        rate-val                                  (rate-k original-fee)
        rate-type                                 (rate-type-k original-fee)
        rate-str                                  (format-rate-value rate-type rate-val currency)]
    (format-rate-with-recurrence rate-str recurrence-k original-fee tr)))

(defn late-fee-date-note
  "Generates date note for penalty/interest fees using stored date fields.
   Shows waived date or effective date based on fee state and type."
  [{:fee/keys    [type amount effective-date waived-date]
    :domain/keys [state]} tr format-date-fn]
  (when (late-fee-types type)
    (cond
      (= state :fee.state/waived)
      (when-let [date-str (format-date-fn waived-date)]
        (tr [:fee.date/waived-at] [date-str]))

      (and (#{:fee.state/unpaid :fee.state/paid} state) (not (zero? amount)))
      (when-let [date-str (format-date-fn effective-date)]
        (tr [:fee.date/from] [date-str])))))

(defn late-fee-description
  "Generates complete title for penalty/interest fees including date note and configuration.
   Format: '<date-note> - <configuration>' or just '<configuration>' if no date note."
  ([fee tr currency format-date-fn]
   (when (late-fee-types (:fee/type fee))
     (let [config-desc (late-fee-config-note fee tr currency)
           date-note   (late-fee-date-note fee tr format-date-fn)]
       (if date-note
         (str date-note " - " config-desc)
         config-desc)))))

(defn paid-fee-ids
  "Extract paid fee IDs from a bill's fees collection"
  [bill]
  (->> (:bill/fees bill)
       (filter (du/in-state-fn :fee.state/paid))
       (map :db/id)))

(defn reversed-fee-ids
  "Extract reversed fee IDs from a bill's fees collection"
  [bill]
  (->> (:bill/fees bill)
       (filter #(= (:domain/state %) :fee.state/reversed))
       (map :db/id)))

(defn fee-title
  "Generates the title for a fee."
  [{:fee/keys [type] :as fee} lang-or-langs tr & {:keys [late-fee-prefix-str]}]
  (if (late-fee-types type)
    (cond->> (tr [type])
      (string? late-fee-prefix-str) (str late-fee-prefix-str " - "))
    (fee-template-title fee lang-or-langs)))

(defn clamp-interest-periods-by-occurrence-limit
  "Calculates how many times interest can be applied based on occurrence limit.
   Checks original fee and the fee itself for the occurrence limit.
   Returns an int in range [0, periods]."
  [fee periods]
  (let [interest-occurrence-limit (or (-> fee :fee/original-fee :fee-template/interest-occurrence-limit)
                                      (-> fee :fee-template/interest-occurrence-limit))
        interest-periods-applied  (or (:fee/interest-periods-applied fee)
                                      0)] ;; Default to 0 if never applied
    (if (nil? interest-occurrence-limit)
      periods
      (min periods
           (- interest-occurrence-limit
              interest-periods-applied)))))

;; Reversal method helper functions
(def ^{:arglists '([reversal-method lang])} reversal-method-title
  "Get reversal method title in a given language"
  (i18n-model/translation-fallback-fn :bill-reversal-method/titles))

(def ^{:arglists '([reversal-method lang])} reversal-method-message
  "Get reversal method message in a given language"
  (i18n-model/translation-fallback-fn :bill-reversal-method/messages))

(def allowed-reversal-method-types
  #{:bill-reversal-method.type/nsf
    :bill-reversal-method.type/void
    :bill-reversal-method.type/refund})

(defn allowed-reversal-method-type?
  "Predicate to check if a reversal method type is allowed for a given bill.
   NSF method requires cheque payment; VOID and REFUND are always allowed."
  [method-type bill]
  (and (allowed-reversal-method-types method-type)
       (case method-type
         :bill-reversal-method.type/nsf
         (boolean (:payment/cheque-number (last-payment bill)))
         :bill-reversal-method.type/refund
         (pos? (bill-amount bill))
         true)))

(defn reversal-method-disabled?
  "Returns true if the reversal method is disabled."
  [reversal-method]
  (= :bill-reversal-method.state/disabled (:domain/state reversal-method)))

(defn reversal-bill? [bill]
  (:bill/previous-bill bill))

(defn draft-reversal-bill?
  "Returns true if the bill is a draft reversal (has previous-bill link and draft state)."
  [bill]
  (and (= :bill.state/draft (:domain/state bill))
       (reversal-bill? bill)))

(defn bill-reversal-method-title
  "Get the reversal method title for a bill.
   For a reversed parent bill: uses :bill/reversal-method directly.
   For a reversal child bill: navigates via :bill/previous-bill to get the reversal method.
   Returns nil if no reversal method is found."
  [bill langs]
  (some-> (or (:bill/reversal-method bill)
              (-> bill :bill/previous-bill :bill/reversal-method))
          (reversal-method-title langs)))

(defn bill-reversal-method-type
  "Get the reversal method type for a bill.
   For a reversed parent bill: uses :bill/reversal-method directly.
   For a reversal child bill: navigates via :bill/previous-bill to get the reversal method.
   Returns nil if no reversal method is found."
  [bill]
  (:bill-reversal-method/type
    (or (:bill/reversal-method bill)
        (-> bill :bill/previous-bill :bill/reversal-method))))

(defn refund-bill? [bill]
  (and (reversal-bill? bill)
       (= :bill-reversal-method.type/refund
          (bill-reversal-method-type bill))))

(defn bill-reversal-method-message
  "Get the reversal method message for a bill.
   For a reversed parent bill: uses :bill/reversal-method directly.
   For a reversal child bill: navigates via :bill/previous-bill to get the reversal method.
   Returns nil if no reversal method is found."
  [bill langs]
  (some-> (or (:bill/reversal-method bill)
              (-> bill :bill/previous-bill :bill/reversal-method))
          (reversal-method-message langs)))

(defn bill-reversal-reason
  "Get the reversal reason for a bill."
  [bill]
  (or (:bill/reversal-reason bill)
      (-> bill :bill/previous-bill :bill/reversal-reason)))
